import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import api from '../api/api';
import { Card } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Textarea } from '../components/ui/textarea';
import { Label } from '../components/ui/label';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from '../components/ui/dialog';
import {
  CheckCircle, ClockCounterClockwise, XCircle, Lightning, Star,
  ArrowRight, Play, Flag, Bell, Question, Funnel,
} from '@phosphor-icons/react';
import { toast } from 'sonner';
import { NewRequestModal } from '../components/NewRequestModal';
import { AppointmentCard } from '../components/AppointmentCard';
import EmailVerificationBanner from '../components/EmailVerificationBanner';

const STATUS_META = {
  pending:      { label: 'In attesa',       color: 'bg-slate-100 text-slate-700', icon: ClockCounterClockwise, description: 'La richiesta è stata creata ma non ancora inoltrata a un professionista.' },
  notified:     { label: 'Notificata',      color: 'bg-blue-100 text-blue-800',   icon: Bell, description: 'La richiesta è stata inoltrata ai professionisti selezionati, in attesa di risposta.' },
  accepted:     { label: 'Accettata',       color: 'bg-indigo-100 text-indigo-800', icon: CheckCircle, description: 'Un professionista ha accettato la tua richiesta: indica data e ora della prestazione per procedere.' },
  in_progress:  { label: 'In corso',        color: 'bg-amber-100 text-amber-800', icon: Play, description: 'La prestazione è in fase di svolgimento.' },
  completed:    { label: 'Completata',      color: 'bg-sky-100 text-sky-800', icon: Flag, description: 'La prestazione è stata completata. Puoi lasciare una recensione.' },
  reviewed:     { label: 'Recensita',       color: 'bg-purple-100 text-purple-800', icon: Star, description: 'Hai lasciato una recensione a questa prestazione. Grazie!' },
  cancelled:    { label: 'Annullata',       color: 'bg-red-100 text-red-800',     icon: XCircle, description: 'La richiesta è stata annullata dal cliente o dal professionista.' },
  escalated_to_admin: { label: 'Gestione admin', color: 'bg-orange-100 text-orange-800', icon: ClockCounterClockwise, description: 'La richiesta è in gestione al team VivaLink.' },
};

// Filter pills (7 status keys the user requested)
const FILTER_KEYS = ['notified', 'in_progress', 'reviewed', 'accepted', 'cancelled', 'completed', 'pending'];

const CLIENT_ACTIONS = {
  notified:    [{ target: 'cancelled', label: 'Annulla', variant: 'outline' }],
  pending:     [{ target: 'cancelled', label: 'Annulla', variant: 'outline' }],
  accepted:    [{ target: 'in_progress', label: 'Segna come iniziata', variant: 'default' }, { target: 'cancelled', label: 'Annulla', variant: 'outline' }],
  in_progress: [{ target: 'completed', label: 'Segna come completata', variant: 'default' }, { target: 'cancelled', label: 'Annulla', variant: 'outline' }],
  completed:   [{ target: 'reviewed', label: 'Lascia recensione', variant: 'default', special: 'review' }],
};

const MyRequestsPage = () => {
  const { user, loading: authLoading } = useAuth();
  const navigate = useNavigate();
  const [requests, setRequests] = useState([]);
  const [loading, setLoading] = useState(true);

  const [reviewOpen, setReviewOpen] = useState(false);
  const [reviewFor, setReviewFor] = useState(null);
  const [reviewRating, setReviewRating] = useState(5);
  const [reviewComment, setReviewComment] = useState('');
  const [savingReview, setSavingReview] = useState(false);

  // Filters
  const [activeStatus, setActiveStatus] = useState('all'); // 'all' | any key of STATUS_META
  const [dateMode, setDateMode] = useState('all'); // 'all' | 'day' | 'month' | 'custom'
  const [customDate, setCustomDate] = useState(''); // YYYY-MM-DD
  const [legendOpen, setLegendOpen] = useState(false);
  const [newReqOpen, setNewReqOpen] = useState(false);
  const [selectedReqId, setSelectedReqId] = useState(null);

  const fetchAll = useCallback(async () => {
    setLoading(true);
    try {
      const r = await api.get('/api/requests');
      setRequests(r.data);
    } catch { toast.error('Errore caricamento richieste'); }
    setLoading(false);
  }, []);

  useEffect(() => {
    if (authLoading) return;
    if (!user) { navigate('/login'); return; }
    fetchAll();
  }, [user, authLoading, navigate, fetchAll]);

  const changeStatus = async (req, target) => {
    if (target === 'cancelled' && !window.confirm('Vuoi davvero annullare questa richiesta?')) return;
    try {
      await api.patch(`/api/requests/${req.request_id}/status`, { status: target });
      toast.success(`Stato: ${STATUS_META[target]?.label || target}`);
      fetchAll();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Errore aggiornamento stato');
    }
  };

  const openReview = (req) => {
    setReviewFor(req); setReviewRating(5); setReviewComment('');
    setReviewOpen(true);
  };
  const saveReview = async () => {
    if (!reviewFor) return;
    setSavingReview(true);
    try {
      await api.post(`/api/requests/${reviewFor.request_id}/review`, {
        rating: reviewRating, comment: reviewComment,
      });
      toast.success('Recensione salvata — grazie!');
      setReviewOpen(false);
      fetchAll();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Errore recensione');
    } finally { setSavingReview(false); }
  };

  if (authLoading || loading) {
    return (
      <div className="min-h-screen bg-background">
        <div className="max-w-5xl mx-auto px-4 py-12">Caricamento…</div>
      </div>
    );
  }

  // Apply filters (status + date)
  const filterByDate = (req) => {
    if (dateMode === 'all') return true;
    const created = req.created_at ? new Date(req.created_at) : null;
    if (!created) return false;
    const now = new Date();
    if (dateMode === 'day') {
      return created.toDateString() === now.toDateString();
    }
    if (dateMode === 'month') {
      return created.getFullYear() === now.getFullYear() && created.getMonth() === now.getMonth();
    }
    if (dateMode === 'custom' && customDate) {
      const [y, m, d] = customDate.split('-').map(Number);
      return created.getFullYear() === y && created.getMonth() + 1 === m && created.getDate() === d;
    }
    return true;
  };
  const filtered = requests
    .filter((r) => activeStatus === 'all' ? true : r.status === activeStatus)
    .filter(filterByDate);

  const selectedReq = filtered.find((r) => r.request_id === selectedReqId) || filtered[0] || null;

  // Count per status (for pill badges)
  const countByStatus = requests.reduce((acc, r) => {
    acc[r.status] = (acc[r.status] || 0) + 1;
    return acc;
  }, {});

  return (
    <div className="min-h-screen bg-background">
      <div className="max-w-5xl mx-auto px-4 py-12" data-testid="my-requests-page">
        <div className="mb-8 flex items-start justify-between flex-wrap gap-3">
          <div>
            <h1 className="font-heading font-semibold text-4xl mb-2">Le mie richieste</h1>
            <p className="text-foreground/70">Storico completo e stato delle tue richieste di assistenza.</p>
          </div>
          <Button className="rounded-full" onClick={() => navigate('/dashboard/nuova-richiesta')} data-testid="new-request-btn">
            Nuova richiesta <ArrowRight size={16} className="ml-1" />
          </Button>
        </div>

        <div className="mb-6">
          <EmailVerificationBanner />
        </div>

        {/* Status filter pills */}
        <div className="mb-4 flex flex-wrap gap-2" data-testid="status-filter-pills">
          <button
            type="button"
            onClick={() => setActiveStatus('all')}
            className={`px-3 py-1.5 rounded-full text-xs font-medium border transition-colors ${activeStatus === 'all' ? 'bg-primary text-primary-foreground border-primary' : 'bg-background text-foreground/70 border-border hover:bg-muted'}`}
            data-testid="filter-status-all"
          >
            Tutte <span className="opacity-70">({requests.length})</span>
          </button>
          {FILTER_KEYS.map((key) => {
            const meta = STATUS_META[key];
            const Icon = meta.icon;
            const active = activeStatus === key;
            const n = countByStatus[key] || 0;
            return (
              <button
                key={key}
                type="button"
                onClick={() => setActiveStatus(key)}
                className={`inline-flex items-center gap-1 px-3 py-1.5 rounded-full text-xs font-medium border transition-colors ${active ? 'bg-primary text-primary-foreground border-primary' : 'bg-background text-foreground/70 border-border hover:bg-muted'}`}
                data-testid={`filter-status-${key}`}
              >
                <Icon size={12} weight="duotone" /> {meta.label} <span className="opacity-70">({n})</span>
              </button>
            );
          })}
        </div>

        {/* Date filter + legend toggle */}
        <div className="mb-6 flex items-center gap-3 flex-wrap" data-testid="date-filter-row">
          <Funnel size={14} className="text-foreground/60" />
          <span className="text-xs text-foreground/60">Filtra per data:</span>
          {[
            { k: 'all',    l: 'Tutte' },
            { k: 'day',    l: 'Oggi' },
            { k: 'month',  l: 'Questo mese' },
          ].map((d) => (
            <button
              key={d.k}
              type="button"
              onClick={() => setDateMode(d.k)}
              className={`px-3 py-1 rounded-full text-xs border transition-colors ${dateMode === d.k ? 'bg-primary text-primary-foreground border-primary' : 'bg-background text-foreground/70 border-border hover:bg-muted'}`}
              data-testid={`filter-date-${d.k}`}
            >
              {d.l}
            </button>
          ))}
          <input
            type="date"
            value={customDate}
            onChange={(e) => { setCustomDate(e.target.value); setDateMode(e.target.value ? 'custom' : 'all'); }}
            className={`text-xs px-2 py-1 border rounded-full ${dateMode === 'custom' ? 'bg-primary/10 border-primary' : 'border-border'}`}
            data-testid="filter-date-custom"
          />
          <button
            type="button"
            onClick={() => setLegendOpen((v) => !v)}
            className="ml-auto inline-flex items-center gap-1 text-xs text-primary hover:underline"
            data-testid="legend-toggle"
          >
            <Question size={14} weight="duotone" /> {legendOpen ? 'Nascondi legenda' : 'Cosa significano gli stati?'}
          </button>
        </div>

        {/* Legend */}
        {legendOpen && (
          <Card className="rounded-2xl p-4 mb-6 bg-muted/30" data-testid="status-legend">
            <div className="text-sm font-semibold mb-3">Legenda stati richiesta</div>
            <div className="grid sm:grid-cols-2 gap-2">
              {FILTER_KEYS.map((k) => {
                const meta = STATUS_META[k];
                const Icon = meta.icon;
                return (
                  <div key={k} className="flex items-start gap-2 text-xs" data-testid={`legend-item-${k}`}>
                    <Badge className={`${meta.color} shrink-0`}>
                      <Icon size={10} className="mr-1" /> {meta.label}
                    </Badge>
                    <span className="text-foreground/70">{meta.description}</span>
                  </div>
                );
              })}
            </div>
          </Card>
        )}

        {requests.length === 0 ? (
          <Card className="neumorphism rounded-2xl p-12 text-center">
            <p className="text-foreground/70 mb-4">Non hai ancora fatto nessuna richiesta.</p>
            <Button className="rounded-full" onClick={() => navigate('/dashboard/nuova-richiesta')} data-testid="cta-first-req">
              Crea la prima
            </Button>
          </Card>
        ) : filtered.length === 0 ? (
          <Card className="rounded-2xl p-8 text-center bg-muted/20" data-testid="empty-filtered">
            <p className="text-foreground/70">Nessuna richiesta corrisponde ai filtri selezionati.</p>
            <Button variant="outline" className="rounded-full mt-3" onClick={() => { setActiveStatus('all'); setDateMode('all'); setCustomDate(''); }} data-testid="reset-filters-btn">
              Rimuovi filtri
            </Button>
          </Card>
        ) : (
          <div className="space-y-4">
            {/* Request chooser dropdown */}
            <div className="space-y-2">
              <Label className="text-xs text-foreground/60">Seleziona una richiesta ({filtered.length})</Label>
              <select
                className="w-full h-11 rounded-xl border border-border bg-background px-3 text-sm outline-none focus:border-primary"
                value={selectedReq?.request_id || ''}
                onChange={(e) => setSelectedReqId(e.target.value)}
                data-testid="request-chooser"
              >
                {filtered.map((r) => {
                  const meta = STATUS_META[r.status] || STATUS_META.pending;
                  const date = new Date(r.created_at).toLocaleDateString('it-IT');
                  const svc = r.service_type || r.need_category || 'Prestazione';
                  const label = (r.status === 'completed' || r.status === 'reviewed')
                    ? `${date} · ${svc} · ${r.professional_name || 'Professionista n/d'}`
                    : `${date} · ${svc} · ${meta.label}${r.is_urgent ? ' · URGENTE' : ''}`;
                  return (
                    <option key={r.request_id} value={r.request_id}>
                      {label}
                    </option>
                  );
                })}
              </select>
            </div>

            {/* Selected request full detail */}
            {selectedReq && (() => {
              const req = selectedReq;
              const meta = STATUS_META[req.status] || STATUS_META.pending;
              const StatusIcon = meta.icon;
              const actions = CLIENT_ACTIONS[req.status] || [];
              return (
                <Card className="neumorphism rounded-2xl p-6" data-testid={`req-detail-${req.request_id}`}>
                  <div className="flex items-start justify-between flex-wrap gap-3 mb-4">
                    <div className="flex items-center gap-2 flex-wrap">
                      <Badge className={meta.color}>
                        <StatusIcon size={12} className="mr-1" /> {meta.label}
                      </Badge>
                      {req.is_urgent && (
                        <Badge className="bg-red-100 text-red-800">
                          <Lightning size={12} className="mr-1" weight="fill" /> Urgente
                        </Badge>
                      )}
                      <span className="text-xs text-foreground/60">{new Date(req.created_at).toLocaleDateString('it-IT')}</span>
                    </div>
                    <div className="text-xs text-foreground/50 font-mono">{req.request_id}</div>
                  </div>

                  <p className="text-xs text-foreground/60 mb-3">{meta.description}</p>

                  <AppointmentCard request={req} onUpdated={fetchAll} />

                  <div className="grid sm:grid-cols-2 gap-3 mb-4 text-sm">
                    <div><span className="text-foreground/50">Prestazione:</span> <strong>{req.service_type || req.need_category || '—'}</strong></div>
                    <div><span className="text-foreground/50">Paziente:</span> <strong>{req.patient_name || '—'}</strong></div>
                    <div><span className="text-foreground/50">Zona:</span> {req.region || '—'}{req.comune ? ` — ${req.comune}` : ''}</div>
                    <div><span className="text-foreground/50">Telefono:</span> {req.contact_phone || '—'}</div>
                    {req.is_patient === false && (
                      <>
                        {req.patient_name && <div data-testid="detail-patient-name"><span className="text-foreground/50">Paziente:</span> {req.patient_name}</div>}
                        {req.patient_phone && <div data-testid="detail-patient-phone"><span className="text-foreground/50">Tel. paziente:</span> {req.patient_phone}</div>}
                        {req.family_phone && <div data-testid="detail-family-phone"><span className="text-foreground/50">Tel. familiare:</span> {req.family_phone}</div>}
                      </>
                    )}
                    {req.professional_name && (
                      <div><span className="text-foreground/50">Professionista:</span> <strong>{req.professional_name}</strong></div>
                    )}
                    {req.assigned_internal_professional && (
                      <div data-testid={`internal-pro-${req.request_id}`}>
                        <span className="text-foreground/50">Operatore del Centro:</span>{' '}
                        <strong>{req.assigned_internal_professional.name}</strong>
                        {req.assigned_internal_professional.profession_code ? ` · ${req.assigned_internal_professional.profession_code}` : ''}
                      </div>
                    )}
                    {req.assigned_internal_pending && (
                      <div className="text-xs text-foreground/60" data-testid={`internal-pending-${req.request_id}`}>
                        Il Centro ha assegnato un professionista: il nominativo sarà visibile alla conferma della data.
                      </div>
                    )}
                    {req.budget != null && <div><span className="text-foreground/50">Budget:</span> {req.budget_type === 'single' ? `€${req.budget} (singola)` : req.budget_type === 'monthly' ? `€${req.budget} (mensile)` : `a partire da €${req.budget}`}</div>}
                    {req.matched_professional_ids?.length > 0 && (
                      <div><span className="text-foreground/50">Professionisti coinvolti:</span> {req.matched_professional_ids.length}</div>
                    )}
                  </div>

                  {(req.needs_labels || []).length > 0 && (
                    <div className="flex flex-wrap gap-1 mb-3">
                      {(req.needs_labels || []).map((l, i) => (
                        <Badge key={i} variant="outline" className="text-xs">{l}</Badge>
                      ))}
                    </div>
                  )}

                  {req.description && (
                    <p className="text-sm text-foreground/80 mb-3 whitespace-pre-line">{req.description}</p>
                  )}

                  {actions.length > 0 && (
                    <div className="flex gap-2 flex-wrap pt-3 border-t border-border/60">
                      {actions.map((a) => (
                        <Button
                          key={a.target}
                          size="sm"
                          variant={a.variant}
                          className="rounded-full"
                          onClick={() => a.special === 'review' ? openReview(req) : changeStatus(req, a.target)}
                          data-testid={`action-${req.request_id}-${a.target}`}
                        >
                          {a.label}
                        </Button>
                      ))}
                    </div>
                  )}
                </Card>
              );
            })()}
          </div>
        )}
      </div>

      {/* New request modal */}
      <NewRequestModal
        open={newReqOpen}
        onOpenChange={setNewReqOpen}
        onCreated={(req) => { if (req?.request_id) { setActiveStatus('all'); setDateMode('all'); setCustomDate(''); setSelectedReqId(req.request_id); } fetchAll(); }}
      />

      {/* Review modal */}
      <Dialog open={reviewOpen} onOpenChange={setReviewOpen}>
        <DialogContent data-testid="review-dialog">
          <DialogHeader>
            <DialogTitle>Lascia una recensione</DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <div>
              <Label className="mb-2 block">Valutazione</Label>
              <div className="flex gap-1" data-testid="rating-stars">
                {[1, 2, 3, 4, 5].map((n) => (
                  <button
                    key={n}
                    type="button"
                    onClick={() => setReviewRating(n)}
                    className="transition-transform hover:scale-110"
                    data-testid={`star-${n}`}
                  >
                    <Star size={32} weight={n <= reviewRating ? 'fill' : 'regular'} className={n <= reviewRating ? 'text-yellow-500' : 'text-foreground/30'} />
                  </button>
                ))}
              </div>
            </div>
            <div>
              <Label>Commento (opzionale)</Label>
              <Textarea
                rows={4}
                value={reviewComment}
                onChange={(e) => setReviewComment(e.target.value)}
                placeholder="Com'è andata l'esperienza?"
                data-testid="review-comment"
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setReviewOpen(false)}>Annulla</Button>
            <Button onClick={saveReview} disabled={savingReview} data-testid="submit-review-btn">
              {savingReview ? 'Invio…' : 'Invia recensione'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default MyRequestsPage;
